import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import api from '../services/api';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, ArcElement, Title, Tooltip, Legend, Filler);

const MODULE_COLORS = {
  truck: { bg: '#2563eb', light: '#dbeafe' },
  ocean_import: { bg: '#0891b2', light: '#cffafe' },
  ocean_export: { bg: '#7c3aed', light: '#ede9fe' },
  air_import: { bg: '#ea580c', light: '#fff7ed' },
  misc: { bg: '#64748b', light: '#f1f5f9' },
};

const formatCurrency = (val) => {
  const num = Number(val) || 0;
  if (Math.abs(num) >= 1000000) return '$' + (num / 1000000).toFixed(1) + 'M';
  if (Math.abs(num) >= 1000) return '$' + (num / 1000).toFixed(1) + 'K';
  return '$' + num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatFullCurrency = (val) => {
  const num = Number(val) || 0;
  return '$' + num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const Reports = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('year');
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => { loadData(); }, [period]);

  const loadData = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/reports?period=${period}`);
      setData(res.data);
    } catch (error) {
      toast.error('Failed to load reports');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !data) {
    return <div className="loading"><div className="loading-spinner"></div></div>;
  }

  const { summary, volumeByModule, monthlyTrend, financialByModule, revenueTrend, arAging, apAging, topCustomers, topRoutes, opPerformance, statusDistribution, chargeBreakdown } = data;

  const tabs = [
    { key: 'overview', label: 'Overview' },
    { key: 'financial', label: 'Financial' },
    { key: 'operations', label: 'Operations' },
    { key: 'aging', label: 'AR/AP Aging' },
    { key: 'performance', label: 'Performance' },
  ];

  return (
    <div>
      {/* Header */}
      <div className="card">
        <div className="card-header" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <h2 className="card-title" style={{ margin: 0 }}>REPORTS & ANALYTICS</h2>
            <div className="filter-group">
              <label className="filter-label">Period:</label>
              <select className="filter-select" value={period} onChange={(e) => setPeriod(e.target.value)}>
                <option value="week">Last 7 Days</option>
                <option value="month">Last 30 Days</option>
                <option value="quarter">Last Quarter</option>
                <option value="half">Last 6 Months</option>
                <option value="year">Last Year</option>
                <option value="all">All Time</option>
              </select>
            </div>
          </div>

          {/* Tab Navigation */}
          <div style={{ display: 'flex', gap: '4px', borderBottom: '2px solid #e2e8f0', width: '100%', paddingBottom: '0' }}>
            {tabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  background: activeTab === tab.key ? '#2563eb' : 'transparent',
                  color: activeTab === tab.key ? '#fff' : '#64748b',
                  fontWeight: 600,
                  fontSize: '12px',
                  borderRadius: '6px 6px 0 0',
                  cursor: 'pointer',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {activeTab === 'overview' && <OverviewTab data={data} />}
      {activeTab === 'financial' && <FinancialTab data={data} />}
      {activeTab === 'operations' && <OperationsTab data={data} />}
      {activeTab === 'aging' && <AgingTab data={data} />}
      {activeTab === 'performance' && <PerformanceTab data={data} />}
    </div>
  );
};

// ===================== OVERVIEW TAB =====================
const OverviewTab = ({ data }) => {
  const { summary, volumeByModule, monthlyTrend, statusDistribution, financialByModule } = data;

  // Shipment volume by module chart
  const volumeChartData = {
    labels: volumeByModule.map(v => v.label),
    datasets: [{
      label: 'Total Shipments',
      data: volumeByModule.map(v => v.total),
      backgroundColor: volumeByModule.map(v => MODULE_COLORS[v.type]?.bg || '#64748b'),
      borderRadius: 6,
      borderSkipped: false,
    }],
  };

  // Monthly trend stacked bar
  const trendChartData = {
    labels: monthlyTrend.map(m => m.month),
    datasets: [
      { label: 'Truck', data: monthlyTrend.map(m => m.truck || 0), backgroundColor: '#2563eb', stack: 'stack0', borderRadius: 2 },
      { label: 'Ocean Import', data: monthlyTrend.map(m => m.ocean_import || 0), backgroundColor: '#0891b2', stack: 'stack0', borderRadius: 2 },
      { label: 'Ocean Export', data: monthlyTrend.map(m => m.ocean_export || 0), backgroundColor: '#7c3aed', stack: 'stack0', borderRadius: 2 },
      { label: 'Air Import', data: monthlyTrend.map(m => m.air_import || 0), backgroundColor: '#ea580c', stack: 'stack0', borderRadius: 2 },
      { label: 'Misc', data: monthlyTrend.map(m => m.misc || 0), backgroundColor: '#64748b', stack: 'stack0', borderRadius: 2 },
    ],
  };

  // Status doughnut
  const statusLabels = Object.keys(statusDistribution);
  const statusColors = statusLabels.map(s => {
    if (s === 'Delivered' || s === 'Completed') return '#059669';
    if (s === 'In Transit' || s === 'Active') return '#2563eb';
    if (s === 'Pending') return '#f59e0b';
    if (s === 'Cancelled' || s === 'Void') return '#dc2626';
    return '#94a3b8';
  });
  const statusChartData = {
    labels: statusLabels,
    datasets: [{
      data: Object.values(statusDistribution),
      backgroundColor: statusColors,
      borderWidth: 2,
      borderColor: '#fff',
    }],
  };

  return (
    <>
      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '12px', marginBottom: '20px' }}>
        <SummaryCard label="Total Shipments" value={summary.totalShipments} color="#1e293b" />
        <SummaryCard label="Period Shipments" value={summary.periodShipments} color="#2563eb" />
        <SummaryCard label="Total Revenue" value={formatCurrency(summary.totalRevenue)} color="#059669" />
        <SummaryCard label="Total Cost" value={formatCurrency(summary.totalCost)} color="#dc2626" />
        <SummaryCard label="Net Profit" value={formatCurrency(summary.totalProfit)} color={summary.totalProfit >= 0 ? '#059669' : '#dc2626'} />
        <SummaryCard label="Profit Margin" value={`${(summary.profitMargin || 0).toFixed(1)}%`} color="#7c3aed" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
        {/* Volume by Module */}
        <div className="card">
          <div className="card-header"><h3 className="card-title" style={{ fontSize: '13px' }}>Shipment Volume by Module</h3></div>
          <div style={{ padding: '16px', height: '300px' }}>
            <Bar data={volumeChartData} options={{
              responsive: true, maintainAspectRatio: false,
              plugins: { legend: { display: false } },
              scales: { y: { beginAtZero: true, ticks: { stepSize: 1 } } },
            }} />
          </div>
        </div>

        {/* Status Distribution */}
        <div className="card">
          <div className="card-header"><h3 className="card-title" style={{ fontSize: '13px' }}>Status Distribution</h3></div>
          <div style={{ padding: '16px', height: '300px', display: 'flex', justifyContent: 'center' }}>
            {statusLabels.length > 0 ? (
              <Doughnut data={statusChartData} options={{
                responsive: true, maintainAspectRatio: false,
                plugins: { legend: { position: 'right', labels: { padding: 12, font: { size: 11 } } } },
                cutout: '55%',
              }} />
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', color: '#94a3b8' }}>No data available</div>
            )}
          </div>
        </div>
      </div>

      {/* Monthly Trend */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div className="card-header"><h3 className="card-title" style={{ fontSize: '13px' }}>Monthly Shipment Trend (12 Months)</h3></div>
        <div style={{ padding: '16px', height: '320px' }}>
          <Bar data={trendChartData} options={{
            responsive: true, maintainAspectRatio: false,
            plugins: { legend: { position: 'bottom', labels: { padding: 15, font: { size: 11 } } } },
            scales: { x: { stacked: true }, y: { stacked: true, beginAtZero: true, ticks: { stepSize: 1 } } },
          }} />
        </div>
      </div>

      {/* Module Breakdown Table */}
      <div className="card">
        <div className="card-header"><h3 className="card-title" style={{ fontSize: '13px' }}>Module Breakdown</h3></div>
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Module</th>
                <th style={{ textAlign: 'right' }}>Total</th>
                <th style={{ textAlign: 'right' }}>In Period</th>
                <th style={{ textAlign: 'right' }}>Revenue</th>
                <th style={{ textAlign: 'right' }}>Cost</th>
                <th style={{ textAlign: 'right' }}>Profit</th>
                <th style={{ textAlign: 'right' }}>Margin</th>
              </tr>
            </thead>
            <tbody>
              {volumeByModule.map((mod, i) => {
                const fin = financialByModule.find(f => f.type === mod.type) || {};
                const margin = fin.revenue > 0 ? ((fin.profit / fin.revenue) * 100).toFixed(1) : '0.0';
                return (
                  <tr key={mod.type}>
                    <td>
                      <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: MODULE_COLORS[mod.type]?.bg, marginRight: 8 }} />
                      {mod.label}
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>{mod.total}</td>
                    <td style={{ textAlign: 'right' }}>{mod.period}</td>
                    <td style={{ textAlign: 'right', color: '#059669' }}>{formatFullCurrency(fin.revenue)}</td>
                    <td style={{ textAlign: 'right', color: '#dc2626' }}>{formatFullCurrency(fin.cost)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: fin.profit >= 0 ? '#059669' : '#dc2626' }}>{formatFullCurrency(fin.profit)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600, color: fin.profit >= 0 ? '#059669' : '#dc2626' }}>{margin}%</td>
                  </tr>
                );
              })}
              <tr style={{ fontWeight: 700, borderTop: '2px solid #e2e8f0', background: '#f8fafc' }}>
                <td>Total</td>
                <td style={{ textAlign: 'right' }}>{summary.totalShipments}</td>
                <td style={{ textAlign: 'right' }}>{summary.periodShipments}</td>
                <td style={{ textAlign: 'right', color: '#059669' }}>{formatFullCurrency(summary.totalRevenue)}</td>
                <td style={{ textAlign: 'right', color: '#dc2626' }}>{formatFullCurrency(summary.totalCost)}</td>
                <td style={{ textAlign: 'right', color: summary.totalProfit >= 0 ? '#059669' : '#dc2626' }}>{formatFullCurrency(summary.totalProfit)}</td>
                <td style={{ textAlign: 'right', color: summary.totalProfit >= 0 ? '#059669' : '#dc2626' }}>{(summary.profitMargin || 0).toFixed(1)}%</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

// ===================== FINANCIAL TAB =====================
const FinancialTab = ({ data }) => {
  const { financialByModule, revenueTrend, summary, chargeBreakdown } = data;

  // Revenue by module bar chart
  const revenueByModuleData = {
    labels: financialByModule.map(f => f.label),
    datasets: [
      {
        label: 'Revenue',
        data: financialByModule.map(f => f.revenue),
        backgroundColor: '#059669',
        borderRadius: 4,
      },
      {
        label: 'Cost',
        data: financialByModule.map(f => f.cost),
        backgroundColor: '#dc2626',
        borderRadius: 4,
      },
      {
        label: 'Profit',
        data: financialByModule.map(f => f.profit),
        backgroundColor: '#2563eb',
        borderRadius: 4,
      },
    ],
  };

  // Revenue trend line chart
  const revenueTrendData = {
    labels: revenueTrend.map(r => r.month),
    datasets: [
      {
        label: 'Revenue',
        data: revenueTrend.map(r => r.revenue),
        borderColor: '#059669',
        backgroundColor: 'rgba(5, 150, 105, 0.08)',
        tension: 0.4,
        fill: true,
        pointRadius: 3,
      },
      {
        label: 'Cost',
        data: revenueTrend.map(r => r.cost),
        borderColor: '#dc2626',
        backgroundColor: 'rgba(220, 38, 38, 0.08)',
        tension: 0.4,
        fill: true,
        pointRadius: 3,
      },
      {
        label: 'Profit',
        data: revenueTrend.map(r => r.profit),
        borderColor: '#2563eb',
        backgroundColor: 'rgba(37, 99, 235, 0.08)',
        tension: 0.4,
        fill: true,
        pointRadius: 3,
      },
    ],
  };

  // Profit by module doughnut
  const profitModuleData = {
    labels: financialByModule.filter(f => f.profit > 0).map(f => f.label),
    datasets: [{
      data: financialByModule.filter(f => f.profit > 0).map(f => f.profit),
      backgroundColor: financialByModule.filter(f => f.profit > 0).map(f => MODULE_COLORS[f.type]?.bg || '#64748b'),
      borderWidth: 2,
      borderColor: '#fff',
    }],
  };

  return (
    <>
      {/* Financial Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '12px', marginBottom: '20px' }}>
        <SummaryCard label="Total Revenue" value={formatCurrency(summary.totalRevenue)} color="#059669" />
        <SummaryCard label="Total Cost" value={formatCurrency(summary.totalCost)} color="#dc2626" />
        <SummaryCard label="Net Profit" value={formatCurrency(summary.totalProfit)} color={summary.totalProfit >= 0 ? '#059669' : '#dc2626'} />
        <SummaryCard label="AR Invoices" value={`${chargeBreakdown?.ar?.count || 0}`} subtitle={formatCurrency(chargeBreakdown?.ar?.total)} color="#0891b2" />
        <SummaryCard label="AP Bills" value={`${chargeBreakdown?.ap?.count || 0}`} subtitle={formatCurrency(chargeBreakdown?.ap?.total)} color="#ea580c" />
      </div>

      {/* Revenue Trend */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div className="card-header"><h3 className="card-title" style={{ fontSize: '13px' }}>Revenue, Cost & Profit Trend (12 Months)</h3></div>
        <div style={{ padding: '16px', height: '350px' }}>
          <Line data={revenueTrendData} options={{
            responsive: true, maintainAspectRatio: false,
            plugins: { legend: { position: 'bottom', labels: { padding: 15, font: { size: 11 } } } },
            scales: {
              y: {
                beginAtZero: true,
                ticks: { callback: (v) => formatCurrency(v) },
              },
            },
          }} />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '20px', marginBottom: '20px' }}>
        {/* Revenue by Module */}
        <div className="card">
          <div className="card-header"><h3 className="card-title" style={{ fontSize: '13px' }}>Revenue vs Cost by Module</h3></div>
          <div style={{ padding: '16px', height: '320px' }}>
            <Bar data={revenueByModuleData} options={{
              responsive: true, maintainAspectRatio: false,
              plugins: { legend: { position: 'bottom', labels: { padding: 15, font: { size: 11 } } } },
              scales: { y: { beginAtZero: true, ticks: { callback: (v) => formatCurrency(v) } } },
            }} />
          </div>
        </div>

        {/* Profit Distribution */}
        <div className="card">
          <div className="card-header"><h3 className="card-title" style={{ fontSize: '13px' }}>Profit by Module</h3></div>
          <div style={{ padding: '16px', height: '320px', display: 'flex', justifyContent: 'center' }}>
            {profitModuleData.labels.length > 0 ? (
              <Doughnut data={profitModuleData} options={{
                responsive: true, maintainAspectRatio: false,
                plugins: { legend: { position: 'bottom', labels: { padding: 10, font: { size: 11 } } } },
                cutout: '50%',
              }} />
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', color: '#94a3b8' }}>No profit data</div>
            )}
          </div>
        </div>
      </div>

      {/* Revenue Trend Table */}
      <div className="card">
        <div className="card-header"><h3 className="card-title" style={{ fontSize: '13px' }}>Monthly Financial Detail</h3></div>
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Month</th>
                <th style={{ textAlign: 'right' }}>Revenue</th>
                <th style={{ textAlign: 'right' }}>Cost</th>
                <th style={{ textAlign: 'right' }}>Profit</th>
                <th style={{ textAlign: 'right' }}>Margin</th>
              </tr>
            </thead>
            <tbody>
              {revenueTrend.map((r, i) => {
                const margin = r.revenue > 0 ? ((r.profit / r.revenue) * 100).toFixed(1) : '0.0';
                return (
                  <tr key={i}>
                    <td style={{ fontWeight: 500 }}>{r.month}</td>
                    <td style={{ textAlign: 'right', color: '#059669' }}>{formatFullCurrency(r.revenue)}</td>
                    <td style={{ textAlign: 'right', color: '#dc2626' }}>{formatFullCurrency(r.cost)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: r.profit >= 0 ? '#059669' : '#dc2626' }}>{formatFullCurrency(r.profit)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600, color: r.profit >= 0 ? '#059669' : '#dc2626' }}>{margin}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

// ===================== OPERATIONS TAB =====================
const OperationsTab = ({ data }) => {
  const { topCustomers, topRoutes, recentActivity, monthlyTrend } = data;

  // Monthly volume line chart
  const volumeLineData = {
    labels: monthlyTrend.map(m => m.month),
    datasets: [{
      label: 'Total Shipments',
      data: monthlyTrend.map(m => m.total),
      borderColor: '#2563eb',
      backgroundColor: 'rgba(37, 99, 235, 0.08)',
      tension: 0.4,
      fill: true,
      pointRadius: 4,
      pointBackgroundColor: '#2563eb',
    }],
  };

  return (
    <>
      {/* Monthly Volume */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div className="card-header"><h3 className="card-title" style={{ fontSize: '13px' }}>Monthly Shipment Volume</h3></div>
        <div style={{ padding: '16px', height: '280px' }}>
          <Line data={volumeLineData} options={{
            responsive: true, maintainAspectRatio: false,
            plugins: { legend: { display: false } },
            scales: { y: { beginAtZero: true, ticks: { stepSize: 1 } } },
          }} />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
        {/* Top Customers */}
        <div className="card">
          <div className="card-header"><h3 className="card-title" style={{ fontSize: '13px' }}>Top Customers by Revenue</h3></div>
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Customer</th>
                  <th style={{ textAlign: 'right' }}>Shipments</th>
                  <th style={{ textAlign: 'right' }}>Revenue</th>
                </tr>
              </thead>
              <tbody>
                {(topCustomers || []).length === 0 ? (
                  <tr><td colSpan={4} style={{ textAlign: 'center', color: '#94a3b8', padding: '24px' }}>No data available</td></tr>
                ) : topCustomers.map((c, i) => (
                  <tr key={i}>
                    <td style={{ fontWeight: 600, color: '#94a3b8' }}>{i + 1}</td>
                    <td style={{ fontWeight: 500 }}>{c.customer}</td>
                    <td style={{ textAlign: 'right' }}>{c.shipment_count}</td>
                    <td style={{ textAlign: 'right', color: '#059669', fontWeight: 600 }}>{formatFullCurrency(c.revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Top Routes */}
        <div className="card">
          <div className="card-header"><h3 className="card-title" style={{ fontSize: '13px' }}>Top Routes</h3></div>
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Route</th>
                  <th style={{ textAlign: 'right' }}>Shipments</th>
                </tr>
              </thead>
              <tbody>
                {(topRoutes || []).length === 0 ? (
                  <tr><td colSpan={3} style={{ textAlign: 'center', color: '#94a3b8', padding: '24px' }}>No data available</td></tr>
                ) : topRoutes.map((r, i) => (
                  <tr key={i}>
                    <td style={{ fontWeight: 600, color: '#94a3b8' }}>{i + 1}</td>
                    <td style={{ fontWeight: 500 }}>{r.route}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>{r.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="card">
        <div className="card-header"><h3 className="card-title" style={{ fontSize: '13px' }}>Recent Activity (All Modules)</h3></div>
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>File No</th>
                <th>Module</th>
                <th>Customer</th>
                <th>Status</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {(data.recentActivity || []).length === 0 ? (
                <tr><td colSpan={5} style={{ textAlign: 'center', color: '#94a3b8', padding: '24px' }}>No recent activity</td></tr>
              ) : data.recentActivity.map((a, i) => (
                <tr key={i}>
                  <td style={{ fontWeight: 500, color: '#2563eb' }}>{a.file_no || '-'}</td>
                  <td>
                    <span style={{
                      background: (MODULE_COLORS[a.shipment_type]?.bg || '#64748b') + '18',
                      color: MODULE_COLORS[a.shipment_type]?.bg || '#64748b',
                      fontWeight: 600, fontSize: '10px', padding: '2px 8px', borderRadius: '10px',
                    }}>
                      {a.module_label}
                    </span>
                  </td>
                  <td>{a.customer || '-'}</td>
                  <td>
                    <span style={{
                      fontWeight: 500, fontSize: '11px', padding: '2px 8px', borderRadius: '10px',
                      background: a.status === 'Delivered' || a.status === 'Completed' ? '#dcfce7' : a.status === 'In Transit' ? '#dbeafe' : '#fef3c7',
                      color: a.status === 'Delivered' || a.status === 'Completed' ? '#059669' : a.status === 'In Transit' ? '#2563eb' : '#d97706',
                    }}>
                      {a.status || 'N/A'}
                    </span>
                  </td>
                  <td style={{ fontSize: '12px', color: '#64748b' }}>{a.created_at ? new Date(a.created_at).toLocaleDateString() : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

// ===================== AGING TAB =====================
const AgingTab = ({ data }) => {
  const { arAging, apAging } = data;

  const agingLabels = ['Current', '1-30 Days', '31-60 Days', '61-90 Days', '90+ Days'];

  const arChartData = {
    labels: agingLabels,
    datasets: [{
      label: 'AR Outstanding ($)',
      data: [arAging.current, arAging.days30, arAging.days60, arAging.days90, arAging.days120],
      backgroundColor: ['#059669', '#f59e0b', '#ea580c', '#dc2626', '#7f1d1d'],
      borderRadius: 4,
      borderSkipped: false,
    }],
  };

  const apChartData = {
    labels: agingLabels,
    datasets: [{
      label: 'AP Outstanding ($)',
      data: [apAging.current, apAging.days30, apAging.days60, apAging.days90, apAging.days120],
      backgroundColor: ['#2563eb', '#7c3aed', '#9333ea', '#6b21a8', '#4c1d95'],
      borderRadius: 4,
      borderSkipped: false,
    }],
  };

  const arTotal = Object.values(arAging).reduce((s, v) => s + v, 0);
  const apTotal = Object.values(apAging).reduce((s, v) => s + v, 0);

  // Doughnut for AR aging breakdown
  const arDoughnut = {
    labels: agingLabels,
    datasets: [{
      data: [arAging.current, arAging.days30, arAging.days60, arAging.days90, arAging.days120],
      backgroundColor: ['#059669', '#f59e0b', '#ea580c', '#dc2626', '#7f1d1d'],
      borderWidth: 2, borderColor: '#fff',
    }],
  };

  const apDoughnut = {
    labels: agingLabels,
    datasets: [{
      data: [apAging.current, apAging.days30, apAging.days60, apAging.days90, apAging.days120],
      backgroundColor: ['#2563eb', '#7c3aed', '#9333ea', '#6b21a8', '#4c1d95'],
      borderWidth: 2, borderColor: '#fff',
    }],
  };

  return (
    <>
      {/* AR/AP Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '20px' }}>
        <SummaryCard label="Total AR Outstanding" value={formatCurrency(arTotal)} color="#059669" />
        <SummaryCard label="AR Overdue (30+)" value={formatCurrency(arAging.days30 + arAging.days60 + arAging.days90 + arAging.days120)} color="#dc2626" />
        <SummaryCard label="Total AP Outstanding" value={formatCurrency(apTotal)} color="#2563eb" />
        <SummaryCard label="AP Overdue (30+)" value={formatCurrency(apAging.days30 + apAging.days60 + apAging.days90 + apAging.days120)} color="#7c3aed" />
      </div>

      {/* AR Aging */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '20px', marginBottom: '20px' }}>
        <div className="card">
          <div className="card-header"><h3 className="card-title" style={{ fontSize: '13px' }}>Accounts Receivable Aging</h3></div>
          <div style={{ padding: '16px', height: '300px' }}>
            <Bar data={arChartData} options={{
              responsive: true, maintainAspectRatio: false,
              plugins: { legend: { display: false } },
              scales: { y: { beginAtZero: true, ticks: { callback: (v) => formatCurrency(v) } } },
            }} />
          </div>
        </div>
        <div className="card">
          <div className="card-header"><h3 className="card-title" style={{ fontSize: '13px' }}>AR Breakdown</h3></div>
          <div style={{ padding: '16px', height: '300px', display: 'flex', justifyContent: 'center' }}>
            {arTotal > 0 ? (
              <Doughnut data={arDoughnut} options={{
                responsive: true, maintainAspectRatio: false,
                plugins: { legend: { position: 'bottom', labels: { font: { size: 10 }, padding: 8 } } },
                cutout: '50%',
              }} />
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', color: '#94a3b8' }}>No AR data</div>
            )}
          </div>
        </div>
      </div>

      {/* AP Aging */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '20px', marginBottom: '20px' }}>
        <div className="card">
          <div className="card-header"><h3 className="card-title" style={{ fontSize: '13px' }}>Accounts Payable Aging</h3></div>
          <div style={{ padding: '16px', height: '300px' }}>
            <Bar data={apChartData} options={{
              responsive: true, maintainAspectRatio: false,
              plugins: { legend: { display: false } },
              scales: { y: { beginAtZero: true, ticks: { callback: (v) => formatCurrency(v) } } },
            }} />
          </div>
        </div>
        <div className="card">
          <div className="card-header"><h3 className="card-title" style={{ fontSize: '13px' }}>AP Breakdown</h3></div>
          <div style={{ padding: '16px', height: '300px', display: 'flex', justifyContent: 'center' }}>
            {apTotal > 0 ? (
              <Doughnut data={apDoughnut} options={{
                responsive: true, maintainAspectRatio: false,
                plugins: { legend: { position: 'bottom', labels: { font: { size: 10 }, padding: 8 } } },
                cutout: '50%',
              }} />
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', color: '#94a3b8' }}>No AP data</div>
            )}
          </div>
        </div>
      </div>

      {/* Aging Detail Table */}
      <div className="card">
        <div className="card-header"><h3 className="card-title" style={{ fontSize: '13px' }}>Aging Summary Table</h3></div>
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Type</th>
                <th style={{ textAlign: 'right' }}>Current</th>
                <th style={{ textAlign: 'right' }}>1-30 Days</th>
                <th style={{ textAlign: 'right' }}>31-60 Days</th>
                <th style={{ textAlign: 'right' }}>61-90 Days</th>
                <th style={{ textAlign: 'right' }}>90+ Days</th>
                <th style={{ textAlign: 'right' }}>Total</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ fontWeight: 600 }}>Accounts Receivable</td>
                <td style={{ textAlign: 'right' }}>{formatFullCurrency(arAging.current)}</td>
                <td style={{ textAlign: 'right', color: '#f59e0b' }}>{formatFullCurrency(arAging.days30)}</td>
                <td style={{ textAlign: 'right', color: '#ea580c' }}>{formatFullCurrency(arAging.days60)}</td>
                <td style={{ textAlign: 'right', color: '#dc2626' }}>{formatFullCurrency(arAging.days90)}</td>
                <td style={{ textAlign: 'right', color: '#7f1d1d', fontWeight: 700 }}>{formatFullCurrency(arAging.days120)}</td>
                <td style={{ textAlign: 'right', fontWeight: 700 }}>{formatFullCurrency(arTotal)}</td>
              </tr>
              <tr>
                <td style={{ fontWeight: 600 }}>Accounts Payable</td>
                <td style={{ textAlign: 'right' }}>{formatFullCurrency(apAging.current)}</td>
                <td style={{ textAlign: 'right', color: '#7c3aed' }}>{formatFullCurrency(apAging.days30)}</td>
                <td style={{ textAlign: 'right', color: '#9333ea' }}>{formatFullCurrency(apAging.days60)}</td>
                <td style={{ textAlign: 'right', color: '#6b21a8' }}>{formatFullCurrency(apAging.days90)}</td>
                <td style={{ textAlign: 'right', color: '#4c1d95', fontWeight: 700 }}>{formatFullCurrency(apAging.days120)}</td>
                <td style={{ textAlign: 'right', fontWeight: 700 }}>{formatFullCurrency(apTotal)}</td>
              </tr>
              <tr style={{ fontWeight: 700, background: '#f8fafc', borderTop: '2px solid #e2e8f0' }}>
                <td>Net (AR - AP)</td>
                <td style={{ textAlign: 'right' }}>{formatFullCurrency(arAging.current - apAging.current)}</td>
                <td style={{ textAlign: 'right' }}>{formatFullCurrency(arAging.days30 - apAging.days30)}</td>
                <td style={{ textAlign: 'right' }}>{formatFullCurrency(arAging.days60 - apAging.days60)}</td>
                <td style={{ textAlign: 'right' }}>{formatFullCurrency(arAging.days90 - apAging.days90)}</td>
                <td style={{ textAlign: 'right' }}>{formatFullCurrency(arAging.days120 - apAging.days120)}</td>
                <td style={{ textAlign: 'right' }}>{formatFullCurrency(arTotal - apTotal)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

// ===================== PERFORMANCE TAB =====================
const PerformanceTab = ({ data }) => {
  const { opPerformance, topCustomers } = data;

  // OP Revenue bar chart
  const opChartData = {
    labels: (opPerformance || []).slice(0, 10).map(o => o.op),
    datasets: [
      {
        label: 'Revenue',
        data: (opPerformance || []).slice(0, 10).map(o => o.revenue),
        backgroundColor: '#059669',
        borderRadius: 4,
      },
      {
        label: 'Cost',
        data: (opPerformance || []).slice(0, 10).map(o => o.cost),
        backgroundColor: '#dc2626',
        borderRadius: 4,
      },
    ],
  };

  // Customer revenue horizontal bar
  const custChartData = {
    labels: (topCustomers || []).slice(0, 10).map(c => c.customer?.substring(0, 20) || 'N/A'),
    datasets: [{
      label: 'Revenue',
      data: (topCustomers || []).slice(0, 10).map(c => c.revenue),
      backgroundColor: '#2563eb',
      borderRadius: 4,
    }],
  };

  return (
    <>
      {/* OP Performance Chart */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div className="card-header"><h3 className="card-title" style={{ fontSize: '13px' }}>Operator Performance — Revenue vs Cost</h3></div>
        <div style={{ padding: '16px', height: '320px' }}>
          {(opPerformance || []).length > 0 ? (
            <Bar data={opChartData} options={{
              responsive: true, maintainAspectRatio: false,
              plugins: { legend: { position: 'bottom', labels: { padding: 15, font: { size: 11 } } } },
              scales: { y: { beginAtZero: true, ticks: { callback: (v) => formatCurrency(v) } } },
            }} />
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#94a3b8' }}>No OP data available</div>
          )}
        </div>
      </div>

      {/* Customer Revenue Chart */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div className="card-header"><h3 className="card-title" style={{ fontSize: '13px' }}>Top Customers by Revenue</h3></div>
        <div style={{ padding: '16px', height: '320px' }}>
          {(topCustomers || []).length > 0 ? (
            <Bar data={custChartData} options={{
              responsive: true, maintainAspectRatio: false,
              indexAxis: 'y',
              plugins: { legend: { display: false } },
              scales: { x: { beginAtZero: true, ticks: { callback: (v) => formatCurrency(v) } } },
            }} />
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#94a3b8' }}>No customer data available</div>
          )}
        </div>
      </div>

      {/* OP Performance Table */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div className="card-header"><h3 className="card-title" style={{ fontSize: '13px' }}>Operator Performance Detail</h3></div>
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Operator</th>
                <th style={{ textAlign: 'right' }}>Shipments</th>
                <th style={{ textAlign: 'right' }}>Revenue</th>
                <th style={{ textAlign: 'right' }}>Cost</th>
                <th style={{ textAlign: 'right' }}>Profit</th>
                <th style={{ textAlign: 'right' }}>Margin</th>
              </tr>
            </thead>
            <tbody>
              {(opPerformance || []).length === 0 ? (
                <tr><td colSpan={7} style={{ textAlign: 'center', color: '#94a3b8', padding: '24px' }}>No data available</td></tr>
              ) : opPerformance.map((o, i) => {
                const margin = o.revenue > 0 ? ((o.profit / o.revenue) * 100).toFixed(1) : '0.0';
                return (
                  <tr key={i}>
                    <td style={{ fontWeight: 600, color: '#94a3b8' }}>{i + 1}</td>
                    <td style={{ fontWeight: 600 }}>{o.op}</td>
                    <td style={{ textAlign: 'right' }}>{o.shipment_count}</td>
                    <td style={{ textAlign: 'right', color: '#059669' }}>{formatFullCurrency(o.revenue)}</td>
                    <td style={{ textAlign: 'right', color: '#dc2626' }}>{formatFullCurrency(o.cost)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: o.profit >= 0 ? '#059669' : '#dc2626' }}>{formatFullCurrency(o.profit)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600, color: o.profit >= 0 ? '#059669' : '#dc2626' }}>{margin}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Customer Detail Table */}
      <div className="card">
        <div className="card-header"><h3 className="card-title" style={{ fontSize: '13px' }}>Customer Revenue Detail</h3></div>
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Customer</th>
                <th style={{ textAlign: 'right' }}>Shipments</th>
                <th style={{ textAlign: 'right' }}>Revenue</th>
                <th style={{ textAlign: 'right' }}>Avg Revenue/Shipment</th>
              </tr>
            </thead>
            <tbody>
              {(topCustomers || []).length === 0 ? (
                <tr><td colSpan={5} style={{ textAlign: 'center', color: '#94a3b8', padding: '24px' }}>No data available</td></tr>
              ) : topCustomers.map((c, i) => (
                <tr key={i}>
                  <td style={{ fontWeight: 600, color: '#94a3b8' }}>{i + 1}</td>
                  <td style={{ fontWeight: 500 }}>{c.customer}</td>
                  <td style={{ textAlign: 'right' }}>{c.shipment_count}</td>
                  <td style={{ textAlign: 'right', color: '#059669', fontWeight: 600 }}>{formatFullCurrency(c.revenue)}</td>
                  <td style={{ textAlign: 'right', color: '#2563eb' }}>
                    {formatFullCurrency(c.shipment_count > 0 ? c.revenue / c.shipment_count : 0)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

// ===================== SHARED COMPONENTS =====================
const SummaryCard = ({ label, value, subtitle, color }) => (
  <div style={{
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: '10px',
    padding: '16px',
    borderLeft: `4px solid ${color}`,
  }}>
    <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', fontWeight: 600, marginBottom: '6px', letterSpacing: '0.3px' }}>{label}</div>
    <div style={{ fontSize: '20px', fontWeight: 700, color }}>{value}</div>
    {subtitle && <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>{subtitle}</div>}
  </div>
);

export default Reports;
